import styled from "styled-components";
import { useMemo, useState } from "react";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import useApi from "../../hooks/useApi";
import { formatCurrency } from "../../Funcoes/formatCurrency";

const Title = styled.h2`
    margin: 0 0 4px 0;
    font-size: 18px;
    color: #333;
`;

const Subtitle = styled.p`
    margin: 0 0 16px 0;
    font-size: 14px;
    color: #555;
`;

const Lista = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 55dvh;
    overflow: auto;
`;

const ParcelaLinha = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 8px;
`;

const ParcelaInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const ParcelaTitulo = styled.span`
    font-size: 14px;
    font-weight: bold;
    color: #333;
`;

const ParcelaDetalhe = styled.span`
    font-size: 12px;
    color: #666;
`;

const InputData = styled.input`
    padding: 8px;
    box-sizing: border-box;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;
    &:focus {
        border-color: #007bff;
        outline: none;
        box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
    }
`;

const EmptyText = styled.p`
    margin: 0;
    font-size: 14px;
    color: #666;
`;

const BotoesWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
`;

const BotaoGerar = styled.button`
    padding: 8px 16px;
    background-color: #007bff;
    color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background-color: #0056b3;
    }
    &:disabled {
        background-color: #9bc4ec;
        cursor: not-allowed;
    }
`;

const ModalParcelas = ({ fecharModal, pedido, valorTotal, parcelas }) => {
    const api = useApi();
    const [pdfLoading, setPdfLoading] = useState(false);
    const [datas, setDatas] = useState({});

    const todasPreenchidas = useMemo(
        () => parcelas.length > 0 && parcelas.every((p) => datas[p.item]),
        [parcelas, datas]
    );

    const handleDataChange = (item, value) => {
        setDatas((prev) => ({ ...prev, [item]: value }));
    };

    const gerarPDF = async () => {
        if (!todasPreenchidas || pdfLoading) return;

        setPdfLoading(true);
        try {
            const body = {
                filial: pedido.FILIAL,
                num_pedido: pedido.NUM_PEDIDO,
                parcelas: parcelas.map((p) => ({ item: p.item, data: datas[p.item] }))
            };

            const response = await api.post(
                "Painel/API/Controladoria/GeneratePDF/index.php",
                body,
                { responseType: "blob" }
            );

            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `Pedido_de_Compra_${pedido.NUM_PEDIDO}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            fecharModal();
        } catch (e) {
            console.log(e);
        } finally {
            setPdfLoading(false);
        }
    };

    return (
        <Overlay state={true}>
            <Modal size="md" header close={fecharModal}>
                <Title>Parcelas do Pagamento</Title>
                <Subtitle>
                    Pedido: <strong>{pedido?.NUM_PEDIDO}</strong> | Valor Total:{" "}
                    <strong>R$ {formatCurrency(valorTotal)}</strong>
                </Subtitle>

                {parcelas.length === 0 ? (
                    <EmptyText>Nenhuma parcela encontrada para a condição de pagamento deste pedido.</EmptyText>
                ) : (
                    <Lista>
                        {parcelas.map((p) => (
                            <ParcelaLinha key={p.item}>
                                <ParcelaInfo>
                                    <ParcelaTitulo>Parcela {p.item}</ParcelaTitulo>
                                    <ParcelaDetalhe>
                                        {p.rateio}% &middot; R$ {formatCurrency(p.valor)}
                                    </ParcelaDetalhe>
                                </ParcelaInfo>
                                <InputData
                                    type="date"
                                    value={datas[p.item] || ""}
                                    onChange={(e) => handleDataChange(p.item, e.target.value)}
                                />
                            </ParcelaLinha>
                        ))}
                    </Lista>
                )}

                <BotoesWrapper>
                    <BotaoGerar onClick={gerarPDF} disabled={!todasPreenchidas || pdfLoading}>
                        {pdfLoading ? "Gerando PDF..." : "Imprimir Pedido de Compra"}
                    </BotaoGerar>
                </BotoesWrapper>
            </Modal>
        </Overlay>
    );
};

export default ModalParcelas;
